<template>
  <div>
    <boiler-room-header class="header" />
    <div class="contents">
      <boiler-room-tabs :boiler-room="boilerRoom" />
      <boiler-room-links class="footer" />
    </div>
  </div>
</template>

<script>
import BoilerRoomHeader from "@/components/organisms/boiler-room/BoilerRoomHeader";
import BoilerRoomLinks from "@/components/organisms/boiler-room/BoilerRoomLinks";
import BoilerRoomTabs from "@/components/organisms/boiler-room/BoilerRoomTabs";
import {
  getAccountData,
  getBoilerRoom,
  getBoilerPrice,
} from "@/utils/boilerRoom";
import Web3 from "web3";
import { WALLET_CONNECT, METAMASK } from "@/utils/constants";
import WalletConnectProvider from "@walletconnect/web3-provider";
import network from "@/domain/boilerRoom/network";
import token from "@/domain/boilerRoom/token";

export default {
  name: "BoilerIndex",
  components: {
    BoilerRoomHeader,
    BoilerRoomLinks,
    BoilerRoomTabs,
  },
  data() {
    return {
      boilerRoomDataInterval: null,
      boilerTokenDataInterval: null,
    };
  },
  computed: {
    account() {
      return this.$store.state.account;
    },
    web3() {
      return this.$store.state.web3;
    },
    boilerRoom() {
      return this.$store.state.boilerRoom.data;
    },
  },
  methods: {
    getBoilerRoomDataInterval(instance, seconds) {
      let accountAddress = this.account.address;
      this.boilerRoomDataInterval = setInterval(() => {
        getBoilerRoom(instance, accountAddress).then((result) => {
          this.$store.dispatch("boilerRoom/update", result);
        });
      }, seconds * 1000);
    },

    getBoilerTokenDataInterval(seconds) {
      this.boilerTokenDataInterval = setInterval(() => {
        getBoilerPrice(this.web3.instance)
          .then((result) => {
            this.$store.dispatch("boilerToken/update", {
              price: result,
            });
          })
          .catch((e) => console.error(e));
      }, seconds * 1000);
    },
    async handleAccountChanged(accounts) {
      if (accounts.length == 0) {
        this.$store.dispatch("onLogout");
      }

      const accountData = await getAccountData(
        this.web3.instance,
        token,
        accounts
      );

      if (accountData) {
        this.$store.dispatch("account/updateAccountData", accountData);

        let result = await getBoilerRoom(
          this.web3.instance,
          this.account.address
        );
        this.$store.dispatch("boilerRoom/update", result);

        this.$store.dispatch("toast/updateToast", {
          type: "success",
          title: "Account Connected",
          message: "Your account is connected!",
        });
      } else {
        this.$store.dispatch("toast/updateToast", {
          type: "error",
          title: "Connect Failed",
          message: "Please check the network!",
        });
      }
    },
    handleChainChanged(chainId) {
      if (!network.chainIds.includes(chainId)) {
        this.$store.dispatch("toast/updateToast", {
          type: "error",
          title: "Connect Failed",
          message: "Please check the network!",
        });
      }
      if (network.chainIds.includes(chainId)) {
        this.$store.dispatch("web3/updateChainId", chainId);
        this.$store.dispatch("toast/updateToast", {
          type: "success",
          title: "Connect Success",
          message: "Successfully switched network.",
        });
      }
    },
    handleDisconnected() {
      this.$store.dispatch("onLogout");
    },
  },
  async mounted() {
    try {
      if (!this.$store.state.isLogin) {
        const provider = new WalletConnectProvider({
          chainId: network.chainIds[0],
          rpc: {
            [network.chainIds[0]]: network.rpcUrls[0],
          },
        });

        if (provider.connected) {
          await provider.disconnect();
          localStorage.removeItem("walletconnect");
        }

        const instance = new Web3(
          new Web3.providers.HttpProvider(network.rpcUrls[0])
        );
        const chainId = await instance.eth.net.getId();

        this.$store.dispatch("web3/update", {
          instance,
          chainId,
          provider: null,
        });
      }

      if (this.$store.state.isLogin) {
        if (sessionStorage.getItem("provider") == WALLET_CONNECT) {
          const provider = new WalletConnectProvider({
            chainId: network.chainIds[0],
            rpc: {
              [network.chainIds[0]]: network.rpcUrls[0],
            },
          });

          if (provider.connected) {
            await provider.disconnect();
          }

          await provider.enable();

          const instance = new Web3(provider);
          const chainId = await instance.eth.net.getId();

          this.$store.dispatch("web3/update", {
            instance,
            chainId,
            provider: WALLET_CONNECT,
          });
        }

        if (sessionStorage.getItem("provider") == METAMASK) {
          if (!window.ethereum) {
            return;
          }

          const instance = new Web3(window.ethereum);
          const chainId = await instance.eth.net.getId();

          this.$store.dispatch("web3/update", {
            instance,
            chainId,
            provider: METAMASK,
          });
        }

        // Subscribe to accounts change
        this.web3.instance.currentProvider.on("accountsChanged", (accounts) => {
          this.handleAccountChanged(accounts);
        });

        // Subscribe to chainId change
        this.web3.instance.currentProvider.on("chainChanged", (chainId) => {
          this.handleChainChanged(chainId);
        });

        // Subscribe to session disconnection
        this.web3.instance.currentProvider.on("disconnect", () => {
          this.handleDisconnected();
        });

        const accountData = await getAccountData(this.web3.instance, token);

        if (accountData) {
          this.$store.dispatch("account/updateAccountData", accountData);

          let result = await getBoilerRoom(
            this.web3.instance,
            this.account.address
          );

          this.$store.dispatch("boilerRoom/update", result);

          this.$store.dispatch("toast/updateToast", {
            type: "success",
            title: "Account Connected",
            message: "Your account is connected!",
          });
        } else {
          this.$store.dispatch("toast/updateToast", {
            type: "error",
            title: "Connect Failed",
            message: "Please check the network!",
          });
        }
      }

      getBoilerPrice(this.web3.instance)
        .then((result) => {
          this.$store.dispatch("boilerToken/update", {
            price: result,
          });
        })
        .catch((e) => console.error(e));

      this.getBoilerTokenDataInterval(30);

      getBoilerRoom(this.web3.instance, this.account.address).then((result) => {
        this.$store.dispatch("boilerRoom/update", result);
      });

      this.getBoilerRoomDataInterval(this.web3.instance, 3);
    } catch (e) {
      console.log(e);
    }
  },
  beforeDestroy() {
    clearInterval(this.boilerRoomDataInterval);
    clearInterval(this.boilerTokenDataInterval);

    if (this.$store.state.isLogin) {
      this.web3.instance.currentProvider.removeListener(
        "accountsChanged",
        this.handleAccountChanged
      );
      this.web3.instance.currentProvider.removeListener(
        "chainChanged",
        this.handleChainChanged
      );
      this.web3.instance.currentProvider.removeListener(
        "disconnect",
        this.handleDisconnected
      );
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
.contents {
  margin: 0 auto;
  max-width: 800px;
  padding-bottom: 120px;
  @include media(sp) {
    width: 94%;
    margin: auto;
  }
}
</style>